import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Trip, TripCost } from "../types/common.types";
import removeTimezone from "./removeTimezone";
import formatCLP from "./formatCLP";

interface Accounting {
  costs: TripCost[];
  grand_total: number;
}

// Reporte de costos para revisar con el proveedor (ej. la cuenta del Cooler con Bárbara): una sola
// tabla con una fila por costo de cada tour, para que un mes entero quepa en una hoja en vez de las
// ~9 que ocupaba con una tabla por tour.
export default function buildCostReportPdf(
  trips: Trip[],
  accounting: Accounting | undefined,
  { costName, fromDate, toDate }: { costName?: string; fromDate: string; toDate: string }
) {
  const doc = new jsPDF();
  const margin = 10;
  const allCosts = !costName || costName === "all";

  doc.setFontSize(13);
  doc.text(`Costos - ${allCosts ? "Todos" : costName}`, margin, 12);
  doc.setFontSize(9);
  doc.text(`Periodo: ${fromDate} - ${toDate}`, margin, 18);

  const rows: string[][] = [];
  let total = 0;
  let totalPassengers = 0;
  trips.forEach((trip) => {
    const date = trip.trip_date ? removeTimezone(trip.trip_date.toString()) : "";
    const passengers = trip.passengers ?? 0;
    totalPassengers += passengers;
    trip.costs.forEach((c) => {
      total += Number(c.value);
      const row = [date, trip.trip_time, trip.tour_id, String(passengers), formatCLP(c.value)];
      if (allCosts) row.splice(4, 0, c.name);
      rows.push(row);
    });
  });

  // Densidad según cuántas filas haya: la gracia del reporte es revisar un mes completo de una sola
  // hoja, así que con muchos tours se achica la letra antes que partir la tabla en varias páginas.
  const density =
    rows.length <= 50 ? { fontSize: 7, cellPadding: 0.9 } :
    rows.length <= 65 ? { fontSize: 6, cellPadding: 0.7 } :
    { fontSize: 5.5, cellPadding: 0.5 };

  const head = ["Fecha", "Hora", "Tour", "Pax", "Valor"];
  if (allCosts) head.splice(4, 0, "Costo");
  const foot = ["TOTAL", "", `${trips.length} tours`, String(totalPassengers), formatCLP(total)];
  if (allCosts) foot.splice(4, 0, "");

  autoTable(doc, {
    startY: 22,
    head: [head],
    body: rows,
    foot: [foot],
    theme: "striped",
    styles: { ...density, overflow: "linebreak", valign: "middle" },
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 16 },
      1: { cellWidth: 11 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 10, halign: "center" },
      [allCosts ? 5 : 4]: { cellWidth: 22, halign: "right" },
    },
    margin: { left: margin, right: margin },
  });

  // Resumen por costo: solo aporta cuando hay varios costos mezclados — con uno solo seleccionado,
  // el total del pie de la tabla ya es el mismo número.
  if (allCosts && accounting && accounting.costs.length > 1) {
    autoTable(doc, {
      startY: (doc as any).lastAutoTable?.finalY + 8,
      head: [["Resumen por costo", "Total"]],
      body: accounting.costs.map((c) => [c.name, formatCLP(c.total)]),
      foot: [["TOTAL GENERAL", formatCLP(accounting.grand_total)]],
      theme: "striped",
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { cellWidth: 30, halign: "right" } },
      margin: { left: margin, right: margin },
    });
  }

  return doc;
}
